import APIManager from 'api/APIManager';
import APIConstant from 'api/APIConstant';
import { parameterToPath } from 'utils';
import {
  DeliveryStatus,
  DepositStatus,
  OrderType,
  PaymentMethod,
  PurchaseStatus,
  YesNo,
} from 'interfaces';

export type ParcelService = {
  parcel_service_id?: number; // 배송/정기배송 idx
  shop_information_id?: number; // 정보 idx
  order_id?: number; // 주문 idx
  coupon_id?: number; // 쿠폰 idx
  user_id?: number; // 유저 idx
  delivery_address?: { [key: string]: Record<string, any> }; // 배송지
  regular_delivery_option?: { [key: string]: Record<string, any> }; // 정기배송옵션
  is_regular_delivery?: (typeof YesNo)[keyof typeof YesNo]; // 정기배송여부
  invoice_number?: string; // 송장번호
  delivery_status?: (typeof DeliveryStatus)[keyof typeof DeliveryStatus]; // 택배상태
  product?: { [key: string]: Record<string, any> }; // 상품 원본 정보
  payment_date?: number; // 결제일
  deposit_status?: (typeof DepositStatus)[keyof typeof DepositStatus]; // 입금상태
  purchase_status?: (typeof PurchaseStatus)[keyof typeof PurchaseStatus]; // 구매상태
  payment_method?: (typeof PaymentMethod)[keyof typeof PaymentMethod]; // 결제수단
  is_present?: (typeof YesNo)[keyof typeof YesNo]; // 선물여부
  type?: (typeof OrderType)[keyof typeof OrderType]; // 타입
  group_id?: string; // 그룹 idx
  num?: number; // 수량
  created_at?: number; // 생성일
  updated_at?: number; // 수정일
  regular_payment_id?: number; // 정기배송 id
};

const $http = new APIManager();

const Api = {
  /**
   * [GETS] ParcelService
   * --
   */
  getParcelServices: async (params?: { [key: string]: string | number }) => {
    try {
      const res = await $http.get(APIConstant.GETS_PARCEL_SERVICE, params);

      return res;
    } catch (e) {
      throw e;
    }
  },

  /**
   * [GET] ParcelService
   * --
   */
  getParcelService: async (
    idx: number,
    params?: { [key: string]: string | number }
  ) => {
    try {
      const url = parameterToPath(APIConstant.GET_PARCEL_SERVICE, {
        parcel_service_id: idx,
      });
      const res = await $http.get(url, params);

      return res;
    } catch (e) {
      throw e;
    }
  },

  /**
   * [POST] ParcelService
   * --
   */
  createParcelService: async (body: ParcelService) => {
    try {
      const res = await $http.post(APIConstant.CREATE_PARCEL_SERVICE, body);

      return res;
    } catch (e) {
      throw e;
    }
  },

  /**
   * [PUT] ParcelService
   * --
   */
  updateParcelService: async (body: ParcelService) => {
    try {
      const { parcel_service_id } = body;
      const url = parameterToPath(APIConstant.UPDATE_PARCEL_SERVICE, {
        parcel_service_id: parcel_service_id,
      });

      const res = await $http.put(url, body);

      return res;
    } catch (e) {
      throw e;
    }
  },

  /**
   * [DELETE] ParcelService
   * --
   */
  deleteParcelService: async (idx: number) => {
    try {
      const url = parameterToPath(APIConstant.UPDATE_PARCEL_SERVICE, {
        parcel_service_id: idx,
      });

      const res = await $http.delete(url);

      return res;
    } catch (e) {
      throw e;
    }
  },

  /**
   * [POST] ParcelService
   * --
   */
  bulkCreateParcelService: async (body: ParcelService[]) => {
    try {
      const res = await $http.post(
        APIConstant.BULK_CREATE_PARCEL_SERVICE,
        body
      );

      return res;
    } catch (e) {
      throw e;
    }
  },

  /**
   * [PUT] ParcelService
   * --
   */
  bulkUpdateParcelService: async (body: ParcelService[]) => {
    try {
      const res = await $http.put(APIConstant.BULK_UPDATE_PARCEL_SERVICE, body);

      return res;
    } catch (e) {
      throw e;
    }
  },

  /**
   * [DELETE] ParcelService
   * --
   */
  bulkDeleteParcelService: async (body: ParcelService[]) => {
    try {
      const res = await $http.post(
        APIConstant.BULK_DELETE_PARCEL_SERVICE,
        body
      );

      return res;
    } catch (e) {
      throw e;
    }
  },

  /**
   * [PUT] ParcelService
   * --
   */
  updateParcelServiceStatus: async (body: ParcelService) => {
    try {
      const { parcel_service_id } = body;
      const url = parameterToPath(APIConstant.UPDATE_PARCEL_SERVICE_STATUS, {
        parcel_service_id: parcel_service_id,
      });

      const res = await $http.put(url, body);

      return res;
    } catch (e) {
      throw e;
    }
  },

  /**
   * [PUT] ParcelService
   * --
   */
  confirmParcelServiceStatus: async (body: ParcelService) => {
    try {
      const { parcel_service_id } = body;
      const url = parameterToPath(APIConstant.CONFIRM_PARCEL_SERVICE_STATUS, {
        parcel_service_id: parcel_service_id,
      });

      const res = await $http.put(url, body);

      return res;
    } catch (e) {
      throw e;
    }
  },

  /**
   * [PUT] ParcelService
   * --
   */
  updateParcelServiceStatusBulk: async (body: ParcelService[]) => {
    try {
      const res = await $http.bulk(
        APIConstant.UPDATE_PARCEL_SERVICE_STATUS_BULK,
        body,
        'PUT'
      );
      return res;
    } catch (e) {
      throw e;
    }
  },
};

export default Api;
